import React from "react";

import { IHealthStatus } from "../../../../services/medical-history/medical-history.interface";

type VitalItem = {
  label: string;
  value: string;
  unit?: string;
  hasValue: boolean;
};

const EMPTY_VALUE = "—";

const isPresent = (value?: string | null): value is string =>
  value !== null && value !== undefined && value.trim() !== "";

const WEIGHT_UNIT_ABBREVIATIONS: Record<string, string> = {
  kilogram: "kg",
  kilograms: "kg",
  kilo: "kg",
  kilos: "kg",
  kg: "kg",
  kgs: "kg",
  pound: "lb",
  pounds: "lb",
  lb: "lb",
  lbs: "lb",
  gram: "g",
  grams: "g",
  g: "g",
};

const abbreviateWeightUnit = (unit?: string | null): string | undefined => {
  if (!isPresent(unit)) {
    return undefined;
  }

  return WEIGHT_UNIT_ABBREVIATIONS[unit.trim().toLowerCase()] ?? unit.trim();
};

const makeVital = (
  label: string,
  raw: string | null | undefined,
  unit?: string,
): VitalItem =>
  isPresent(raw) ?
    { label, value: raw, unit, hasValue: true } :
    { label, value: EMPTY_VALUE, hasValue: false };

const buildVitals = (healthStatus: IHealthStatus): VitalItem[] => [
  makeVital(
    "Weight",
    healthStatus.weight,
    abbreviateWeightUnit(healthStatus.weightUnits),
  ),
  makeVital(
    "Temperature",
    healthStatus.temperature,
    isPresent(healthStatus.temperatureUnit) ? healthStatus.temperatureUnit : undefined,
  ),
  makeVital("Heart rate", healthStatus.heartRate, "bpm"),
  makeVital("Breathing", healthStatus.respiratoryRate, "/min"),
  makeVital(
    "Body condition",
    healthStatus.bcs,
    isPresent(healthStatus.maxBcs) ? `/${healthStatus.maxBcs}` : undefined,
  ),
  makeVital(
    "Dental",
    healthStatus.dentalScore,
    isPresent(healthStatus.maxDentalScore) ?
      `/${healthStatus.maxDentalScore}` :
      undefined,
  ),
];

type Props = {
  healthStatus: IHealthStatus;
};

const COLUMNS = 3;

const VitalsGrid = (props: Props) => {
  const { healthStatus } = props;
  const vitals = buildVitals(healthStatus);
  const totalRows = Math.ceil(vitals.length / COLUMNS);

  return (
    <div className="grid grid-cols-3 overflow-hidden rounded-[20px] border border-taupe-50 bg-white">
      {vitals.map((vital, index) => {
        const column = index % COLUMNS;
        const row = Math.floor(index / COLUMNS);
        const hasRightDivider = column < COLUMNS - 1;
        const hasBottomDivider = row < totalRows - 1;

        return (
          <div
            key={vital.label}
            className={`flex flex-col gap-1 px-3 pt-3.5 pb-3 bg-white ${
              hasRightDivider ? "border-r border-gray-2" : ""
            } ${hasBottomDivider ? "border-b border-gray-2" : ""}`}
          >
            <p className="geograph-regular text-[12px] leading-[14px] text-gray-1">
              {vital.label}
            </p>
            <div className="geograph-medium flex items-baseline gap-[2px] whitespace-nowrap">
              <span
                className={`text-[18px] leading-[20px] ${
                  vital.hasValue ? "text-light-black" : "text-gray-1"
                }`}
              >
                {vital.value}
              </span>
              {vital.unit && (
                <span className="text-[11px] leading-[14px] text-gray-1">
                  {vital.unit}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VitalsGrid;
